import { useState } from "react";
import {
  AlertTriangle,
  ClipboardCheck,
  Home,
  Sprout,
  Trash2,
  Wrench,
} from "lucide-react";
import HygieneMdmSchoolsModal from "@/components/HygieneMdmSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  avg,
  buildHygieneActionQueue,
  buildHygieneClosurePlan,
  fmt,
  pct,
} from "@/utils/waterSafetyDerived";
import WaterSafetyGuidanceChip from "@/components/WaterSafetyGuidanceChip";
import { toTitleCase } from "../utils/toTitleCase";

const buildMdmGaugeStyle = (value) => {
  const color =
    value >= 90 ? "var(--inf-success)" : value >= 75 ? "var(--inf-warning)" : "var(--inf-danger)";
  const track =
    value >= 90
      ? "rgba(16,185,129,.14)"
      : value >= 75
        ? "rgba(245,158,11,.14)"
        : "rgba(239,68,68,.14)";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, ${track} ${Math.min(value, 100)}% 100%)`,
  };
};

const buildClassroomDonutStyle = (allPct, somePct) => {
  const endAll = Math.min(allPct, 100);
  const endSome = Math.min(endAll + somePct, 100);
  return {
    background: `conic-gradient(var(--inf-success) 0 ${endAll}%, var(--inf-warning) ${endAll}% ${endSome}%, var(--inf-danger) ${endSome}% 100%)`,
  };
};

const getMdmRiskLabel = (score) => {
  if (score >= 90) return "Low Hygiene Risk";
  if (score >= 75) return "Moderate Hygiene Risk";
  return "High Hygiene Risk";
};

const getHygieneStatus = (score) => {
  if (score >= 85) return { label: "Stable", tone: "green" };
  if (score >= 70) return { label: "Watch", tone: "" };
  if (score >= 50) return { label: "Priority", tone: "red" };
  return { label: "Critical", tone: "red" };
};

const getHygieneAction = (block) => {
  const shed = block.kitchen_shed_pct ?? 0;
  const garden = block.kitchen_garden_pct ?? 0;
  const kitchen = block.kitchen_dustbin_pct ?? 0;

  if (shed < 40 || block.hygiene_score < 40) return "Immediate block review";
  if (shed < 55 && garden < 50) return "MDM infra sprint";
  if (shed < 65) return "Kitchen shed closure";
  if (garden < 75) return "Garden scale-up";
  if (kitchen < 85) return "Improve kitchen dustbin coverage";
  if (block.toilet_hygiene_pct < 95) return "Maintain hygiene cadence";
  return "Document best practices";
};

const LegendItem = ({ tone, title, description, value }) => (
  <div className="inf-legend-item">
    <span className={`inf-legend-dot ${tone}`} />
    <div className="inf-legend-copy">
      <b>{title}</b>
      <span>{description}</span>
    </div>
    <strong>{fmt(value)}</strong>
  </div>
);

const ReadinessCard = ({
  icon: Icon,
  iconTone,
  pillLabel,
  pillTone,
  title,
  value,
  unit,
  progressPct,
  progressColor,
  metaLeft,
  metaRight,
}) => (
  <article className="inf-readiness-card">
    <div className="inf-readiness-head">
      <div className={`inf-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <span className={`inf-pill ${pillTone}`}>{pillLabel}</span>
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="inf-readiness-score">
      <strong>{value}</strong>
      <span>{unit}</span>
    </div>
    <div className="inf-progress-line">
      <i style={{ width: `${Math.min(progressPct, 100)}%`, background: progressColor }} />
    </div>
    <div className="inf-readiness-meta">
      <span>{metaLeft}</span>
      <span>{metaRight}</span>
    </div>
  </article>
);

const WaterSafetyHygieneMdmTab = ({
  overview,
  blockData,
  hygieneDistribution,
  guidance,
  guidanceProvider = "local",
  guidanceLoading = false,
  scopeLabel = "Pune District",
}) => {
  const [hygieneMetricModal, setHygieneMetricModal] = useState(null);

  if (!overview) {
    return (
      <div className="inf-empty-card">
        <Trash2 className="mx-auto mb-3 opacity-40" size={40} />
        <p>No hygiene & MDM data available for the selected scope.</p>
      </div>
    );
  }

  const classroomPct = overview.classroom_hygiene_coverage ?? 0;
  const toiletPct = overview.toilet_hygiene_coverage ?? 0;
  const kitchenPct = overview.kitchen_hygiene_coverage ?? 0;
  const shedPct = overview.kitchen_shed_coverage ?? 0;
  const gardenPct = overview.kitchen_garden_coverage ?? 0;

  const mdmScore = avg(classroomPct, toiletPct, kitchenPct, shedPct, gardenPct);
  const basicHygiene = avg(classroomPct, toiletPct);
  const kitchenDustbinGap = 100 - kitchenPct;
  const shedGap = Math.max(0, (overview.total_schools ?? 0) - (overview.kitchen_shed_yes ?? 0));
  const localActionQueue = buildHygieneActionQueue(overview, blockData);
  const actionQueue = (guidance?.action_queue?.length ? guidance.action_queue : localActionQueue).map((action, index) => ({
    title: action.title,
    description: action.description,
    priority: action.priority ?? {
      label: action.priority_label ?? "Medium",
      tone: action.priority_label === "Critical" ? "high" : action.priority_label === "High" ? "medium" : "low",
    },
  }));
  const heroSummary =
    guidance?.hero_summary ??
    `MDM score is ${mdmScore.toFixed(1)} with classroom dustbin at ${pct(classroomPct)} and kitchen dustbin at ${pct(kitchenPct)}. ${fmt(shedGap)} schools lack kitchen shed and ${fmt(Math.max(0, (overview.total_schools ?? 0) - (overview.kitchen_garden_yes ?? 0)))} lack kitchen garden coverage.`;
  const primaryAction =
    guidance?.primary_action ??
    `Close ${fmt(shedGap)} kitchen shed gaps and ${kitchenDustbinGap.toFixed(1)}% kitchen dustbin gap (${fmt(Math.max(0, (overview.total_schools ?? 0) - (overview.kitchen_dustbin_yes ?? 0)))} schools) in lowest hygiene-score blocks first.`;
  const closurePlanText = guidance?.closure_plan ?? buildHygieneClosurePlan(overview, blockData);

  const classAll = overview.classroom_dustbin_all ?? 0;
  const classSome = overview.classroom_dustbin_some ?? 0;
  const classNo = overview.classroom_dustbin_no ?? 0;
  const classTotal = classAll + classSome + classNo || 1;
  const classAllShare = (classAll / classTotal) * 100;
  const classSomeShare = (classSome / classTotal) * 100;

  const classDist = hygieneDistribution?.classroom ?? [
    { name: "All classrooms", value: classAll, color: "#10b981" },
    { name: "Some classrooms", value: classSome, color: "#f59e0b" },
    { name: "Not available", value: classNo, color: "#ef4444" },
  ];

  const mdmFacilities = [
    { label: "Toilet Dustbin", value: toiletPct, tone: "" },
    { label: "Kitchen Dustbin", value: kitchenPct, tone: "amber" },
    { label: "Kitchen Shed", value: shedPct, tone: "amber" },
    { label: "Kitchen Garden", value: gardenPct, tone: "amber" },
  ];

  const rankedBlocks = [...(blockData ?? [])].sort(
    (a, b) => (b.kitchen_infra_score ?? 0) - (a.kitchen_infra_score ?? 0)
  );

  const tableBlocks = [...(blockData ?? [])]
    .sort((a, b) => (b.hygiene_score ?? 0) - (a.hygiene_score ?? 0))
    .slice(0, 12);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="inf-water-tab inf-hygiene-tab" data-testid="tab-hygiene-content" id="inf-hygiene-overview">
      <section className="inf-hero-card inf-hero-card-hygiene">
        <div className="inf-hero-layout">
          <div>
            <div className="inf-health-main">
              <div
                className="inf-wsi-gauge"
                style={buildMdmGaugeStyle(mdmScore)}
                aria-label={`MDM Hygiene Readiness ${mdmScore.toFixed(1)} percent`}
              >
                <div>
                  <strong>{mdmScore.toFixed(1)}</strong>
                  <span>MDM Score</span>
                </div>
              </div>
              <div className="inf-hero-copy">
                <h2>Hygiene & MDM Command Centre</h2>
                <p>{heroSummary}</p>
                <div className="inf-status-row">
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    onClick={mdmScore < 90 ? () => scrollTo("inf-hygiene-actions") : undefined}
                    ariaLabel={
                      mdmScore < 90 ? `${getMdmRiskLabel(mdmScore)} — view hygiene action queue` : undefined
                    }
                  >
                    {getMdmRiskLabel(mdmScore)}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="green"
                    onClick={classNo > 0 ? () => setHygieneMetricModal("classroom_dustbin") : undefined}
                    ariaLabel={
                      classNo > 0
                        ? `Classroom dustbin ${pct(classroomPct)} — view schools below coverage`
                        : undefined
                    }
                  >
                    Classroom dustbin {pct(classroomPct)}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="inf-status-chip"
                    variant="blue"
                    onClick={
                      classroomPct < 100 || toiletPct < 100
                        ? () => setHygieneMetricModal("toilet_dustbin")
                        : undefined
                    }
                    ariaLabel={
                      classroomPct < 100 || toiletPct < 100
                        ? `Toilet dustbin ${pct(toiletPct)} — view schools below coverage`
                        : undefined
                    }
                  >
                    Toilet dustbin {pct(toiletPct)}
                  </HeroStatusChip>
                  <WaterSafetyGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                </div>
              </div>
            </div>
            <div className="inf-summary-note">
              <div className="inf-icon-box orange">
                <Home size={22} />
              </div>
              <div>
                <b>Primary action for officers</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="inf-metric-grid" aria-label="Hygiene and MDM KPIs">
            <article
              className="inf-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Classroom Dustbin: view schools without full classroom coverage"
              onClick={() => setHygieneMetricModal("classroom_dustbin")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setHygieneMetricModal("classroom_dustbin");
                }
              }}
            >
              <small>Classroom Dustbin</small>
              <strong>{pct(classroomPct)}</strong>
              <span>{fmt(classAll)} all classrooms</span>
            </article>
            <article
              className="inf-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Toilet Dustbin: view schools without toilet dustbin"
              onClick={() => setHygieneMetricModal("toilet_dustbin")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setHygieneMetricModal("toilet_dustbin");
                }
              }}
            >
              <small>Toilet Dustbin</small>
              <strong>{pct(toiletPct)}</strong>
              <span>{fmt(overview.toilet_dustbin_yes)} schools</span>
            </article>
            <article
              className="inf-metric-card warn clickable"
              role="button"
              tabIndex={0}
              aria-label="Kitchen Dustbin: view schools without kitchen dustbin"
              onClick={() => setHygieneMetricModal("kitchen_dustbin")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setHygieneMetricModal("kitchen_dustbin");
                }
              }}
            >
              <small>Kitchen Dustbin</small>
              <strong>{pct(kitchenPct)}</strong>
              <span>{fmt(overview.kitchen_dustbin_yes)} schools</span>
            </article>
            <article
              className="inf-metric-card warn clickable"
              role="button"
              tabIndex={0}
              aria-label="Kitchen Shed and Garden: view schools with MDM infrastructure gap"
              onClick={() => setHygieneMetricModal("kitchen_shed_garden")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setHygieneMetricModal("kitchen_shed_garden");
                }
              }}
            >
              <small>Kitchen Shed / Garden</small>
              <strong>
                {shedPct.toFixed(1)}% / {gardenPct.toFixed(1)}%
              </strong>
              <span>Main MDM infrastructure gap</span>
            </article>
          </div>
        </div>
      </section>

      <div className="inf-section-title" id="inf-hygiene-diagnostics">
        <div>
          <h2>Hygiene Availability Diagnostics</h2>
          <p>
            Instead of showing isolated charts, the redesign groups each facility into a clear availability,
            gap and action view.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-hygiene-actions")}>
          View action queue →
        </button>
      </div>

      <section className="inf-diagnostic-grid">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Classroom Dustbin Distribution</h3>
              <p>
                Most schools report dustbins in all classrooms. The remaining schools need quick verification
                and closure.
              </p>
            </div>
            <span className="inf-pill inf-pill-green">{pct(classroomPct)} ready</span>
          </div>
          <div className="inf-donut-layout">
            <div
              className="inf-donut classroom"
              style={buildClassroomDonutStyle(classAllShare, classSomeShare)}
            >
              <div className="inf-donut-center">
                <div>
                  <strong>{pct(classroomPct)}</strong>
                  <span>Classroom</span>
                </div>
              </div>
            </div>
            <div className="inf-legend-list">
              {classDist.map((item) => {
                const tone =
                  item.name.toLowerCase().includes("not") || item.name.toLowerCase().includes("none")
                    ? "red"
                    : item.name.toLowerCase().includes("some")
                      ? "orange"
                      : "";
                const desc =
                  item.name === "All classrooms"
                    ? "Fully available"
                    : item.name === "Some classrooms"
                      ? "Partial coverage"
                      : "Immediate follow-up";
                return (
                  <LegendItem key={item.name} tone={tone} title={item.name} description={desc} value={item.value} />
                );
              })}
            </div>
          </div>
        </article>

        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>MDM Facility Readiness</h3>
              <p>Kitchen infrastructure is the largest hygiene improvement opportunity in the current view.</p>
            </div>
            <span className="inf-pill inf-pill-amber">MDM focus</span>
          </div>
          <div className="inf-bar-list" aria-label="MDM facility readiness bar chart">
            {mdmFacilities.map((item) => (
              <div className="inf-hbar-row" key={item.label}>
                <div className="inf-hbar-label">{item.label}</div>
                <div className="inf-hbar-track">
                  <div
                    className={`inf-hbar ${item.tone}`}
                    style={{ width: `${Math.min(item.value, 100)}%` }}
                  />
                </div>
                <div className="inf-hbar-value">{pct(item.value)}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="inf-section-title" id="inf-hygiene-readiness">
        <div>
          <h2>Decision Snapshot</h2>
          <p>Quickly separates stable hygiene controls from MDM infrastructure gaps that require officer ownership.</p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-hygiene-ranking")}>
          Go to block ranking →
        </button>
      </div>

      <section className="inf-readiness-grid">
        <ReadinessCard
          icon={ClipboardCheck}
          iconTone="green"
          pillLabel="Stable"
          pillTone="inf-pill-green"
          title="Basic hygiene availability"
          value={basicHygiene.toFixed(1)}
          unit="%"
          progressPct={basicHygiene}
          progressColor="var(--inf-success)"
          metaLeft="Classroom + toilet bins"
          metaRight="Maintain"
        />
        <ReadinessCard
          icon={Home}
          iconTone="orange"
          pillLabel="Needs push"
          pillTone="inf-pill-amber"
          title="Kitchen infrastructure"
          value={shedPct.toFixed(1)}
          unit="%"
          progressPct={shedPct}
          progressColor="var(--inf-secondary)"
          metaLeft="Kitchen shed readiness"
          metaRight="Gap closure"
        />
        <ReadinessCard
          icon={Sprout}
          iconTone="cyan"
          pillLabel="Low adoption"
          pillTone="inf-pill-amber"
          title="Kitchen garden coverage"
          value={gardenPct.toFixed(1)}
          unit="%"
          progressPct={gardenPct}
          progressColor="var(--inf-cyan)"
          metaLeft={`${fmt(overview.kitchen_garden_yes)} schools`}
          metaRight="Scale-up plan"
        />
      </section>

      <div className="inf-section-title" id="inf-hygiene-ranking">
        <div>
          <h2>Block-Wise Hygiene and MDM Ranking</h2>
          <p>
            The chart area now highlights which blocks are safe, watchlisted, and priority for kitchen
            infrastructure improvement.
          </p>
        </div>
        <button type="button" className="inf-section-action" onClick={() => scrollTo("inf-hygiene-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="inf-ranking-grid">
        <article className="inf-panel">
          <div className="inf-panel-head">
            <div>
              <h3>Kitchen Infrastructure Readiness</h3>
              <p>
                Indicative kitchen shed and garden readiness by block, converted from a crowded column chart
                into readable ranking bars.
              </p>
            </div>
            <span className="inf-pill">Block ranking</span>
          </div>
          <div className="inf-bar-list" aria-label="Block-wise kitchen infrastructure bar chart">
            {rankedBlocks.map((block) => {
              const score = block.kitchen_infra_score ?? 0;
              const barTone = score >= 80 ? "" : "amber";
              return (
                <div className="inf-hbar-row" key={block.block_name}>
                  <div className="inf-hbar-label">{block.block_name}</div>
                  <div className="inf-hbar-track">
                    <div className={`inf-hbar ${barTone}`} style={{ width: `${Math.min(score, 100)}%` }} />
                  </div>
                  <div className="inf-hbar-value">{Math.round(score)}</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="inf-panel" id="inf-hygiene-actions">
          <div className="inf-panel-head">
            <div>
              <h3>Officer Action Queue</h3>
              <p>Prioritized actions for weekly hygiene and MDM review.</p>
            </div>
            <span className="inf-pill inf-pill-amber">30-day sprint</span>
          </div>
          <div className="inf-action-card">
            {actionQueue.map((action, index) => {
              const icons = [AlertTriangle, Wrench, Sprout, ClipboardCheck];
              const tones = ["red", "orange", "cyan", "green"];
              const Icon = icons[index] ?? ClipboardCheck;
              return (
                <div className="inf-action-item" key={action.title}>
                  <div className={`inf-icon-box ${tones[index] ?? "green"}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                  <span className={`inf-priority ${action.priority.tone}`}>{action.priority.label}</span>
                </div>
              );
            })}
          </div>
          <div className="inf-plan-card">
            <b>30-day closure plan</b>
            <span>{closurePlanText}</span>
          </div>
        </article>
      </section>

      <section className="inf-panel inf-table-card">
        <div className="inf-table-head">
          <div>
            <h3>Block Action Tracker</h3>
            <p>Use this table to move from observation to accountability, timeline and closure status.</p>
          </div>
          <span className="inf-pill">Operational tracker</span>
        </div>
        <div className="inf-table-wrap">
          <table className="inf-data-table" aria-label="Block-wise hygiene and MDM action tracker">
            <thead>
              <tr>
                <th>Block</th>
                <th>Hygiene Score</th>
                <th>Toilet Dustbin</th>
                <th>Kitchen Dustbin</th>
                <th>Kitchen Shed</th>
                <th>Kitchen Garden</th>
                <th>Priority Action</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableBlocks.map((block) => {
                const score = block.hygiene_score ?? 0;
                const status = getHygieneStatus(score);
                return (
                  <tr key={block.block_name}>
                    <td>{block.block_name}</td>
                    <td className="score">{score.toFixed(1)}</td>
                    <td>{pct(block.toilet_hygiene_pct, 0)}</td>
                    <td>{pct(block.kitchen_dustbin_pct ?? 0, 0)}</td>
                    <td>{pct(block.kitchen_shed_pct, 0)}</td>
                    <td>{pct(block.kitchen_garden_pct ?? 0, 0)}</td>
                    <td>{getHygieneAction(block)}</td>
                    <td>
                      <span className={`inf-tag ${status.tone}`}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <HygieneMdmSchoolsModal
        open={Boolean(hygieneMetricModal)}
        metric={hygieneMetricModal}
        onClose={() => setHygieneMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          hygieneMetricModal === "classroom_dustbin"
            ? classroomPct
            : hygieneMetricModal === "toilet_dustbin"
              ? toiletPct
              : hygieneMetricModal === "kitchen_dustbin"
                ? kitchenPct
                : hygieneMetricModal === "kitchen_shed_garden"
                  ? { kitchen_shed: shedPct, kitchen_garden: gardenPct }
                  : null
        }
      />
    </div>
  );
};

export default WaterSafetyHygieneMdmTab;
